(() => {
  const config = window.MagBakExperiment;
  if (!config || config.surface !== 'landing_page' || !config.id || !config.variant) return;

  const query = new URLSearchParams(window.location.search);
  const production = /(^|\.)magbak\.com$/i.test(window.location.hostname)
    && !query.has('preview_theme_id')
    && !window.Shopify?.designMode;
  const clean = (value, max = 100) => String(value ?? '').replace(/\s\s+/g, ' ').trim().slice(0, max);
  const measurementSessionId = (() => {
    const storageKey = 'mb_measurement_session_v1';
    const now = Date.now();
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
      if (saved?.id && now - Number(saved.touchedAt || 0) < 30 * 60 * 1000) {
        window.sessionStorage.setItem(storageKey, JSON.stringify({ id: saved.id, touchedAt: now }));
        return clean(saved.id, 80);
      }
      const id = window.crypto?.randomUUID?.()
        || `${now.toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
      window.sessionStorage.setItem(storageKey, JSON.stringify({ id, touchedAt: now }));
      return id;
    } catch (error) {
      return `${now.toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
    }
  })();
  const base = () => ({
    experiment_id: clean(config.id, 40),
    experiment_variant: clean(config.variant, 40),
    measurement_session_id: measurementSessionId,
    rendered_variant: clean(config.renderedVariant || config.variant, 40),
    experiment_surface: 'landing_page',
    page_identifier: clean(config.pageId || window.location.pathname, 100),
  });
  const send = (eventName, params = {}) => {
    const detail = { ...base(), ...params };
    document.dispatchEvent(new CustomEvent(`mb:${eventName}`, { detail }));
    if (!production) return;
    if (typeof window.Shopify?.analytics?.publish === 'function') {
      try {
        Promise.resolve(window.Shopify.analytics.publish('magbak_experiments:event', {
          event_name: eventName,
          ...detail,
        })).catch(() => {});
      } catch (error) {}
    }
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, { ...detail, send_to: 'G-PX9LQSC0QQ' });
    } else {
      window.dataLayer.push({ event: eventName, ...detail });
    }
  };

  if (production && typeof window.gtag === 'function') {
    window.gtag('set', 'user_properties', {
      mb_gallery_experiment: clean(config.id, 36),
      mb_gallery_variant: clean(config.variant, 36),
    });
  }

  const exposureTarget = document.querySelector('[data-mb-experiment-surface]') || document.querySelector('main');
  let exposed = false;
  const expose = () => {
    if (exposed) return;
    exposed = true;
    send('experiment_exposure', {
      assigned_variant: clean(config.variant, 40),
      eligible_media_count: Number(config.eligibleMediaCount || 0),
      media_fallback_used: config.fallbackUsed ? 'yes' : 'no',
    });
    if ((config.renderedVariant && config.renderedVariant !== config.variant) || config.fallbackUsed) {
      send('experiment_integrity', {
        assigned_variant: clean(config.variant, 40),
        integrity_status: config.renderedVariant !== config.variant ? 'variant_mismatch' : 'media_fallback',
      });
    }
  };
  if (exposureTarget && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
        observer.disconnect();
        expose();
      }
    }, { threshold: 0.5 });
    observer.observe(exposureTarget);
  } else {
    requestAnimationFrame(expose);
  }

  document.addEventListener('click', (event) => {
    const cta = event.target.closest?.('[data-mb-experiment-cta]');
    if (!cta) return;
    send('landing_cta_click', {
      cta_id: clean(cta.dataset.mbExperimentCta || cta.id || 'primary', 60),
      cta_location: clean(cta.dataset.mbCtaLocation || 'page', 40),
      cta_destination: clean(cta.getAttribute('href') || '', 100),
    });
  });

  if ('IntersectionObserver' in window) {
    const viewedSections = new Set();
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
        const sectionId = clean(entry.target.dataset.mbExperimentSection || entry.target.id, 60);
        if (!sectionId || viewedSections.has(sectionId)) return;
        viewedSections.add(sectionId);
        send('content_section_view', { content_section: sectionId });
        sectionObserver.unobserve(entry.target);
      });
    }, { threshold: 0.5 });
    document.querySelectorAll('[data-mb-experiment-section]').forEach((section) => sectionObserver.observe(section));
  }

  const reached = new Set();
  const trackDepth = () => {
    const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    const percent = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
    [50, 75, 90].forEach((milestone) => {
      if (percent < milestone || reached.has(milestone)) return;
      reached.add(milestone);
      send('landing_scroll_depth', { scroll_percent: milestone });
    });
  };
  window.addEventListener('scroll', trackDepth, { passive: true });
})();
